import {
  RANGER_WEAPONS,
  getDistanceBetweenPoints,
  getMovementAimVector,
  getMovementFacingDirection,
  getPatrolFacingDirection,
  getPatrolMotion,
  getTerrainSpeedMultiplier,
  getVectorFacingDirection,
  normalizeVector,
  playerReachedFlag,
  shouldFireWeapon,
} from '../rangerLogic';
import { getRangerClassConfig } from '../rangerClasses';
import { createMissionCompletePayload } from '../rangerEvents';
import {
  ENEMY_ALERT_DURATION_MS,
  ENEMY_ATTACK_COOLDOWN_MS,
  ENEMY_TURN_PAUSE_MS,
  PLAYER_SPEED,
} from '../rangerTuning';
import {
  createAlly,
  createDecoration,
  createEnemy,
  createFlag,
  createGrenadeCooldownHud,
  createGround,
  createObstacle,
  createPlayer,
  createTarget,
  createTerrain,
  drawSightCone,
  syncEnemyDetails,
  syncPlayerDetails,
  updateGrenadeCooldownHud,
} from './missionRenderers';
import {
  createEnemyShot,
  damagePlayer,
  executePlayerMelee,
  explodeGrenade,
  firePlayerWeapon,
  freezeMissionScene,
  handlePlayerProjectileHit,
  updateProjectiles,
} from './missionCombat';
import {
  createImpactEffect,
  createLastKnownMarker,
  createMissionCompleteEffect,
  createMovementDust,
  createObjectivePulse,
  createScorePopup,
  playRangerSound,
  showToast,
} from './missionEffects';
import {
  enemyCanSeePlayer,
  getVisionBlockers,
  setEnemyAwareness,
  spreadEnemyAlert,
} from './missionEnemies';

function completeMission(scene) {
  if (scene.isComplete) {
    return;
  }

  scene.isComplete = true;
  scene.player.body.setVelocity(0, 0);
  scene.physics.world.pause();
  createMissionCompleteEffect(scene);
  scene.add.text(scene.level.width / 2, scene.level.height / 2, 'Mission Complete', {
    fontFamily: 'monospace',
    fontSize: '28px',
    color: '#f5f8ff',
    stroke: '#182414',
    strokeThickness: 6,
  }).setOrigin(0.5).setDepth(4);
  createScorePopup(scene, scene.level.width / 2, scene.level.height / 2 + 34, 'Mission clear', '#f5f8ff');

  if (scene.onMissionComplete) {
    scene.onMissionComplete(createMissionCompletePayload({
      level: scene.level,
      levelIndex: scene.levelIndex,
      selectedClassId: scene.selectedPlayableIcon,
      elapsedMs: scene.time.now - scene.startTimeMs,
      alertCount: scene.alertCount,
      enemyDefeatCount: scene.enemyDefeatCount,
      remainingLives: scene.playerLives,
    }));
  }
}

function canCompleteLevel(scene) {
  if (scene.level.objectiveType === 'rescue') {
    return scene.rescuedAlly;
  }

  if (scene.level.objectiveType === 'sabotage' || scene.level.objectiveType === 'multi-sabotage') {
    return scene.completedTargets.size >= scene.targets.length;
  }

  return true;
}

function getPlayerAimVector(scene, movementVector) {
  return getMovementAimVector({
    movementVector,
    fallbackVector: scene.playerAimVector,
  });
}

function consumeQueuedControllerAction(controllerInput, actionName) {
  const queuedKey = `${actionName}Queued`;

  if (!controllerInput?.[queuedKey]) {
    return false;
  }

  controllerInput[queuedKey] = false;
  return true;
}

function destroyGameObject(gameObject) {
  gameObject?.destroy?.();
}

function getPhysicsGroupChildren(group) {
  if (Array.isArray(group?.children?.entries)) {
    return [...group.children.entries];
  }

  if (typeof group?.getChildren === 'function') {
    try {
      const children = group.getChildren();
      return Array.isArray(children) ? [...children] : [];
    } catch {
      return [];
    }
  }

  return [];
}

function clearPhysicsGroup(group) {
  if (!group) {
    return;
  }

  getPhysicsGroupChildren(group).forEach((child) => destroyGameObject(child));

  if (Array.isArray(group.children?.entries) && typeof group.clear === 'function') {
    group.clear(false, false);
  }
}

export function cleanupRangerMissionScene(scene) {
  if (scene.cleanedUp) {
    return;
  }

  scene.cleanedUp = true;
  scene.meleeKey?.off?.('down', scene.onMeleeKeyDown);
  scene.onMeleeKeyDown = null;
  scene.time?.removeAllEvents?.();
  scene.cameras?.main?.stopShake?.();
  scene.tweens?.killAll?.();
  scene.physics?.world?.resume?.();
  clearPhysicsGroup(scene.playerProjectiles);
  clearPhysicsGroup(scene.enemyProjectiles);
  scene.enemies?.forEach((enemy) => {
    enemy.enemyData?.visionGraphic?.destroy?.();
    enemy.enemyData?.alertMarker?.destroy?.();
  });
  destroyGameObject(scene.toastText);
  scene.toastText = null;
}

export function createMissionScene({
  controllerInputRef,
  initialLives = 5,
  level,
  levelIndex = 0,
  onGameOver,
  onLivesChange,
  onMissionComplete,
  reducedMotion = false,
  selectedPlayableIcon = 'ranger',
}) {
  return {
    key: 'RangerMissionScene',

    init(data) {
      this.level = data.level ?? level;
      this.levelIndex = data.levelIndex ?? levelIndex;
      this.selectedPlayableIcon = data.selectedPlayableIcon ?? selectedPlayableIcon;
      this.playerClassConfig = getRangerClassConfig(this.selectedPlayableIcon);
      this.controllerInputRef = controllerInputRef;
      this.isComplete = false;
      this.isGameOver = false;
      this.alertCount = 0;
      this.enemyDefeatCount = 0;
      this.playerLives = data.initialLives ?? initialLives;
      this.onGameOver = data.onGameOver ?? onGameOver;
      this.onLivesChange = data.onLivesChange ?? onLivesChange;
      this.onMissionComplete = data.onMissionComplete ?? onMissionComplete;
      this.reducedMotion = data.reducedMotion ?? reducedMotion;
      this.rescuedAlly = false;
      this.completedTargets = new Set();
      this.cleanedUp = false;
    },

    create() {
      const cleanup = () => cleanupRangerMissionScene(this);
      const { obstacles, playerSpawn, flag, decorations = [], terrains = [] } = this.level;

      this.events.once('shutdown', cleanup);
      this.events.once('destroy', cleanup);
      this.cameras.main.setBackgroundColor('#7b764d');

      createGround(this, this.level);
      this.cursors = this.input.keyboard.createCursorKeys();
      this.wasd = this.input.keyboard.addKeys('W,A,S,D');
      this.spaceKey = this.input.keyboard.addKey('SPACE');
      this.grenadeKey = this.input.keyboard.addKey('E');
      this.grenadeAltKey = this.input.keyboard.addKey('SHIFT');
      this.meleeKey = this.input.keyboard.addKey('F');
      this.pendingMeleeInput = false;
      this.onMeleeKeyDown = () => {
        this.pendingMeleeInput = true;
      };
      this.meleeKey.on('down', this.onMeleeKeyDown);
      this.startTimeMs = this.time.now;
      this.playerFacingDirection = 'down';
      this.playerAimVector = { x: 0, y: 1 };
      this.lastWeaponFireTimes = {
        rifle: -RANGER_WEAPONS.rifle.cooldownMs,
        grenade: -RANGER_WEAPONS.grenade.cooldownMs,
        melee: -RANGER_WEAPONS.melee.cooldownMs,
      };
      this.nextProjectileId = 1;
      this.visionBlockers = getVisionBlockers(this.level);
      this.onLivesChange?.(this.playerLives);
      terrains.forEach((terrain) => createTerrain(this, terrain));
      decorations.forEach((decoration) => createDecoration(this, decoration));

      this.obstacles = this.physics.add.staticGroup();
      obstacles.forEach((obstacle) => {
        const obstacleSprite = createObstacle(this, obstacle);
        this.obstacles.add(obstacleSprite);
      });

      this.flag = createFlag(this, flag);
      if (this.level.ally) {
        this.ally = createAlly(this, this.level.ally);
      }
      this.targets = (this.level.targets ?? (this.level.target ? [this.level.target] : []))
        .map((target, index) => createTarget(this, target, index));
      this.enemies = (this.level.enemies ?? []).map((enemy, index) => createEnemy(this, enemy, index));
      createPlayer(this, playerSpawn, this.selectedPlayableIcon);
      createGrenadeCooldownHud(this);
      this.playerProjectiles = this.physics.add.group();
      this.enemyProjectiles = this.physics.add.group();

      this.physics.add.collider(this.player, this.obstacles);
      this.physics.add.collider(this.playerProjectiles, this.obstacles, (projectile) => {
        if (projectile.getData('weaponType') === 'grenade') {
          explodeGrenade(this, projectile);
          return;
        }

        createImpactEffect(this, projectile.x, projectile.y, 0xe6c47c, 12);
        projectile.destroy();
      });
      this.physics.add.collider(this.enemyProjectiles, this.obstacles, (projectile) => {
        createImpactEffect(this, projectile.x, projectile.y, 0xff926b, 10);
        projectile.destroy();
      });
      this.physics.add.overlap(this.player, this.enemyProjectiles, (player, projectile) => {
        projectile.destroy();
        damagePlayer(this);
      });
      this.physics.add.overlap(this.player, this.flag, () => {
        if (!canCompleteLevel(this)) {
          if (this.level.objectiveType === 'rescue') {
            createObjectivePulse(this, this.ally ?? this.flag, 0x9ed7bb);
            showToast(this, 'Find the scout first');
            return;
          }
          this.targets.forEach((target) => createObjectivePulse(this, target, 0xffd06b));
          showToast(this, 'Disable the objective first');
          return;
        }

        completeMission(this);
      });
      if (this.ally) {
        this.physics.add.overlap(this.player, this.ally, () => {
          if (!this.rescuedAlly) {
            this.rescuedAlly = true;
            this.allyVisual.body.setFillStyle(0xaed4a2);
            this.allyVisual.head.setFillStyle(0xc5e2b9);
            createObjectivePulse(this, this.ally, 0x9ed7bb);
            createScorePopup(this, this.ally.x, this.ally.y - 34, 'Rescue', '#d4ffd2');
            playRangerSound('powerup');
            showToast(this, 'Scout rescued. Reach the flare.');
          }
        });
      }
      this.targets.forEach((target, index) => {
        this.physics.add.overlap(this.player, target, () => {
          if (!this.completedTargets.has(index)) {
            this.completedTargets.add(index);
            target.setFillStyle(0x595d48, 0.85);
            createObjectivePulse(this, target, 0xffd06b);
            createScorePopup(this, target.x, target.y - 34, 'Disabled', '#ffe7a3');
            playRangerSound('explosion');
            showToast(this, this.targets.length > 1 ? `Relay ${index + 1} disabled` : 'Depot disabled. Reach extraction.');
          }
        });
      });
      this.enemies.forEach((enemy) => {
        this.physics.add.overlap(this.playerProjectiles, enemy, (projectile, hitEnemy) => {
          handlePlayerProjectileHit(this, projectile, hitEnemy);
        });
        this.physics.add.overlap(this.player, enemy, () => {
          if (enemy.enemyData.defeated || this.time.now - (this.lastAlertTime ?? 0) < 900) {
            return;
          }

          this.lastAlertTime = this.time.now;
          damagePlayer(this, 'Patrol contact. Back to the LZ.');
        });
      });
    },

    update(_time, delta) {
      if (!this.player || this.isComplete || this.isGameOver) {
        return;
      }

      if (this.controllerInputRef?.current?.paused) {
        freezeMissionScene(this);
        return;
      }

      const moveX = (this.cursors.right.isDown || this.wasd.D.isDown ? 1 : 0) -
        (this.cursors.left.isDown || this.wasd.A.isDown ? 1 : 0);
      const moveY = (this.cursors.down.isDown || this.wasd.S.isDown ? 1 : 0) -
        (this.cursors.up.isDown || this.wasd.W.isDown ? 1 : 0);
      const controllerInput = this.controllerInputRef?.current;
      const controllerMoveX = Math.abs(controllerInput?.moveX ?? 0) > 0.16 ? controllerInput.moveX : 0;
      const controllerMoveY = Math.abs(controllerInput?.moveY ?? 0) > 0.16 ? controllerInput.moveY : 0;
      const combinedMoveX = controllerMoveX || moveX;
      const combinedMoveY = controllerMoveY || moveY;
      const isMoving = combinedMoveX !== 0 || combinedMoveY !== 0;
      const movementVector = normalizeVector({ x: combinedMoveX, y: combinedMoveY });
      const terrainMultiplier = getTerrainSpeedMultiplier(this.player, this.level.terrains);
      const playerSpeed = PLAYER_SPEED * terrainMultiplier * (this.playerClassConfig?.speedMultiplier ?? 1);
      const aimVector = getPlayerAimVector(this, movementVector);

      this.player.body.setVelocityX(movementVector.x * playerSpeed);
      this.player.body.setVelocityY(movementVector.y * playerSpeed);
      this.playerAimVector = aimVector;
      this.playerFacingDirection = getMovementFacingDirection({
        movementVector,
        aimVector,
        fallbackDirection: this.playerFacingDirection,
      });
      syncPlayerDetails(this, isMoving);
      if (isMoving) {
        createMovementDust(this);
      }

      if (shouldFireWeapon({
        actionPressed: this.spaceKey.isDown || controllerInput?.primary,
        currentTimeMs: this.time.now,
        lastFireTimeMs: this.lastWeaponFireTimes.rifle,
        cooldownMs: RANGER_WEAPONS.rifle.cooldownMs,
      })) {
        this.lastWeaponFireTimes.rifle = this.time.now;
        firePlayerWeapon(this, 'rifle', aimVector);
      }

      if (shouldFireWeapon({
        actionPressed: this.grenadeKey.isDown || this.grenadeAltKey.isDown || controllerInput?.secondary,
        currentTimeMs: this.time.now,
        lastFireTimeMs: this.lastWeaponFireTimes.grenade,
        cooldownMs: RANGER_WEAPONS.grenade.cooldownMs,
      })) {
        this.lastWeaponFireTimes.grenade = this.time.now;
        firePlayerWeapon(this, 'grenade', aimVector);
      }

      const meleeQueued = consumeQueuedControllerAction(controllerInput, 'melee') || this.pendingMeleeInput;
      this.pendingMeleeInput = false;

      if (shouldFireWeapon({
        actionPressed: this.meleeKey.isDown || controllerInput?.melee || meleeQueued,
        currentTimeMs: this.time.now,
        lastFireTimeMs: this.lastWeaponFireTimes.melee,
        cooldownMs: RANGER_WEAPONS.melee.cooldownMs,
      })) {
        this.lastWeaponFireTimes.melee = this.time.now;
        executePlayerMelee(this, aimVector);
      }

      updateProjectiles(this, delta);
      updateGrenadeCooldownHud(this);

      this.enemies?.forEach((enemy) => {
        if (enemy.enemyData.defeated) {
          enemy.body.setVelocity(0, 0);
          return;
        }

        const data = enemy.enemyData;
        const canSeePlayer = enemyCanSeePlayer(this, enemy);

        if (canSeePlayer && data.awarenessState === 'patrol') {
          showToast(this, 'Movement spotted. Patrol investigating.');
          setEnemyAwareness(enemy, 'investigating', this, { x: this.player.x, y: this.player.y });
          createLastKnownMarker(this, data.lastKnownPlayer);
          createScorePopup(this, enemy.x, enemy.y - 44, '?', '#ffd06b');
          playRangerSound('powerup');
          spreadEnemyAlert(this, enemy);
        } else if (canSeePlayer && data.awarenessState === 'investigating' && this.time.now >= (data.investigateUntil ?? 0)) {
          showToast(this, 'Confirmed contact. Patrol opening fire.');
          setEnemyAwareness(enemy, 'attacking', this, { x: this.player.x, y: this.player.y });
          createScorePopup(this, enemy.x, enemy.y - 44, '!', '#ffb070');
          playRangerSound('damage');
          spreadEnemyAlert(this, enemy);
        } else if (canSeePlayer && data.awarenessState === 'attacking') {
          data.lastKnownPlayer = { x: this.player.x, y: this.player.y };
          data.alertedUntil = this.time.now + (data.alertDurationMs ?? ENEMY_ALERT_DURATION_MS);
        } else if (!canSeePlayer && data.awarenessState !== 'patrol' && this.time.now > (data.alertedUntil ?? 0)) {
          data.awarenessState = 'patrol';
          data.lastKnownPlayer = null;
          data.investigateUntil = 0;
        }

        if (data.awarenessState === 'investigating' && data.lastKnownPlayer) {
          const investigateVector = normalizeVector({
            x: data.lastKnownPlayer.x - enemy.x,
            y: data.lastKnownPlayer.y - enemy.y,
          });

          data.facingDirection = getVectorFacingDirection(investigateVector, data.facingDirection);
          if (getDistanceBetweenPoints(enemy, data.lastKnownPlayer) > 18) {
            enemy.body.setVelocity(
              investigateVector.x * data.speed * 0.62,
              investigateVector.y * data.speed * 0.62
            );
          } else {
            enemy.body.setVelocity(0, 0);
          }
        } else if (data.awarenessState === 'attacking') {
          const attackVector = normalizeVector({
            x: this.player.x - enemy.x,
            y: this.player.y - enemy.y,
          });

          data.facingDirection = getVectorFacingDirection(attackVector, data.facingDirection);
          enemy.body.setVelocity(0, 0);
        } else {
          if (data.pendingFacingDirection && this.time.now >= (data.waitUntilMs ?? 0)) {
            data.facingDirection = data.pendingFacingDirection;
            data.pendingFacingDirection = null;
          }
          const motion = getPatrolMotion({
            ...data,
            currentTimeMs: this.time.now,
          }, enemy);

          data.direction = motion.direction;
          data.facingDirection = motion.facingDirection;
          if (motion.shouldStartTurnWait) {
            data.waitUntilMs = this.time.now + (data.turnPauseMs ?? ENEMY_TURN_PAUSE_MS);
            data.pendingFacingDirection = getPatrolFacingDirection({
              ...data,
              direction: motion.direction,
            });
          }
          enemy.body.setVelocity(motion.velocityX, motion.velocityY);
        }

        const isAlerted = data.awarenessState !== 'patrol' && (data.alertedUntil ?? 0) > this.time.now;
        data.alertMarker.setVisible(isAlerted);
        syncEnemyDetails(enemy);
        drawSightCone(this, enemy);

        if (data.awarenessState === 'attacking' && canSeePlayer && this.time.now >= (data.nextShotTime ?? 0)) {
          data.nextShotTime = this.time.now + (data.attackCooldownMs ?? ENEMY_ATTACK_COOLDOWN_MS);
          createEnemyShot(this, enemy);
        }
      });

      if (playerReachedFlag(this.player, this.level.flag) && canCompleteLevel(this)) {
        completeMission(this);
      }
    },
  };
}
